import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, AlertCircle, AlertTriangle, CheckCircle2, ArrowRight,
  ListChecks, MessageSquare, FileText, Calendar, Clock, DollarSign, User
} from 'lucide-react';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';

/**
 * Full Analysis Modal - Pro Tool Design
 * Matches the three-layer design system:
 * - Clean white background (Work Layer)
 * - Subtle gray accents
 * - Professional, information-dense layout
 */
const FullAnalysisModal = ({
  isOpen,
  onClose,
  brief,
  email,
  onOpenInWord,
  onGenerateReply,
  onCreateTasks
}: { isOpen: any; onClose: any; brief: any; email: any; onOpenInWord?: any; onGenerateReply: any; onCreateTasks?: any }) => {
  const { t } = useTranslation('inbox');
  
  if (!isOpen || !brief) return null;
  
  // Extract data
  const redFlags = brief?.red_flags || [];
  const yellowFlags = brief?.yellow_flags || [];
  const mustClarify = brief?.must_clarify || [];
  const workPlan = brief?.work_plan || [];
  const knownFacts = brief?.known_facts || brief?.what_we_know || brief?.key_facts || [];
  const nextSteps = brief?.next_steps || [];
  const recommendedActions = brief?.recommended_actions || [];
  const missingDocs = brief?.missing_docs || [];
  const customerPriorities = brief?.customer_priorities || [];
  const intakeHeader = brief?.intake_header || {};
  const deadline = brief?.deadline || {};
  
  const hasRisks = redFlags.length > 0 || yellowFlags.length > 0;
  const hasClarifications = mustClarify.length > 0;
  const hasWorkPlan = workPlan.length > 0 || nextSteps.length > 0 || recommendedActions.length > 0;
  const totalRisks = redFlags.length + yellowFlags.length;

  // Check if we have key metrics to show
  const hasDeadline = deadline?.date || brief.deadline_detected;
  const hasWorkEstimate = brief.work_estimate;
  const hasValue = brief.suggested_price;
  const hasMetrics = hasDeadline || hasWorkEstimate || hasValue;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-xl"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">{t('triagePanel.aiAnalysis')}</p>
                <h2 className="text-base font-semibold text-gray-900 leading-tight line-clamp-2">{email?.subject || t('fullAnalysis.emailAnalysis')}</h2>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg hover:bg-gray-200 flex items-center justify-center transition-colors flex-shrink-0 -mt-1 -mr-1"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            
            {/* Tags row */}
            <div className="flex items-center gap-2 mt-3">
              {brief.category && (
                <span className="px-2 py-0.5 bg-white text-gray-600 text-xs font-medium rounded border border-gray-200">
                  {brief.category}
                </span>
              )}
              {brief.priority && (
                <span className={cn(
                  "px-2 py-0.5 text-xs font-medium rounded",
                  brief.priority === 'URGENT' ? 'bg-red-100 text-red-700' :
                  brief.priority === 'HIGH' ? 'bg-amber-100 text-amber-700' :
                  'bg-gray-100 text-gray-600'
                )}>
                  {brief.priority === 'URGENT' ? t('fullAnalysis.urgent') : 
                   brief.priority === 'HIGH' ? t('fullAnalysis.highPriority') : brief.priority}
                </span>
              )}
              {totalRisks > 0 && (
                <span className="px-2 py-0.5 bg-red-50 text-red-600 text-xs font-medium rounded flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {totalRisks} {totalRisks === 1 ? t('fullAnalysis.riskSingular') : t('fullAnalysis.riskPlural')}
                </span>
              )}
            </div>
          </div>
          
          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-180px)]">
            <div className="p-6 space-y-6">
              
              {/* Summary */}
              <div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {brief.what_this_is || brief.summary || t('fullAnalysis.noDescription')}
                </p>
              </div>
              
              {/* Key Metrics Row */}
              {hasMetrics && (
                <div className="flex items-stretch gap-4 py-4 border-y border-gray-100">
                  {hasDeadline && (
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-4 h-4 text-gray-500" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">{t('fullAnalysis.deadline')}</p>
                        <p className="text-sm font-medium text-gray-900">{deadline?.date || brief.deadline_detected}</p>
                      </div>
                    </div>
                  )}
                  {hasWorkEstimate && (
                    <div className="flex items-center gap-3 flex-1 border-l border-gray-100 pl-4">
                      <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-4 h-4 text-gray-500" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">{t('fullAnalysis.estimatedWork')}</p>
                        <p className="text-sm font-medium text-gray-900">{brief.work_estimate}</p>
                      </div>
                    </div>
                  )}
                  {hasValue && (
                    <div className="flex items-center gap-3 flex-1 border-l border-gray-100 pl-4">
                      <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <DollarSign className="w-4 h-4 text-gray-500" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">{t('fullAnalysis.estimatedValue')}</p>
                        <p className="text-sm font-medium text-gray-900">
                          {typeof brief.suggested_price === 'number' 
                            ? `${brief.suggested_price.toLocaleString()} kr`
                            : brief.suggested_price
                          }
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Contact */}
              {(brief.sender_org || intakeHeader.sender_org || brief.sender_name || intakeHeader.sender_name) && (
                <div>
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-2">{t('triagePanel.sender')}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gray-900 flex items-center justify-center text-white font-medium text-sm flex-shrink-0">
                      {(brief.sender_name || intakeHeader.sender_name || 'U')[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {brief.sender_name || intakeHeader.sender_name || email?.from?.name || t('fullAnalysis.unknown')}
                      </p>
                      {(brief.sender_org || intakeHeader.sender_org || brief.sender_role || intakeHeader.sender_role) && (
                        <p className="text-xs text-gray-500 truncate">
                          {brief.sender_org || intakeHeader.sender_org || brief.sender_role || intakeHeader.sender_role}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Customer Priorities */}
              {customerPriorities.length > 0 && (
                <div>
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-2">{t('fullAnalysis.customerPriorities')}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {customerPriorities.map((priority: string, idx: number) => (
                      <span key={idx} className="px-2.5 py-1 bg-gray-100 text-gray-700 text-xs rounded-md">
                        {priority}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Key Facts */}
              {knownFacts.length > 0 && (
                <div>
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-2">{t('fullAnalysis.keyFacts')}</p>
                  <div className="space-y-1.5">
                    {knownFacts.map((fact: string, idx: number) => (
                      <div key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{fact}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Risk Flags */}
              {hasRisks && (
                <div>
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-2">
                    {t('fullAnalysis.risks')}
                  </p>
                  <div className="space-y-2">
                    {redFlags.map((flag: any, idx: number) => (
                      <div key={`red-${idx}`} className="p-3 rounded-lg border border-red-200 bg-red-50/50">
                        <div className="flex items-start gap-2.5">
                          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            {flag.clause && (
                              <p className="text-[10px] font-semibold text-red-600 uppercase tracking-wide mb-0.5">
                                {flag.clause}
                              </p>
                            )}
                            <p className="text-sm text-gray-900">{flag.issue}</p>
                            {flag.recommendation && (
                              <p className="text-xs text-gray-500 mt-1 flex items-start gap-1">
                                <ArrowRight className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                <span>{flag.recommendation}</span>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {yellowFlags.map((flag: any, idx: number) => (
                      <div key={`yellow-${idx}`} className="p-3 rounded-lg border border-amber-200 bg-amber-50/50">
                        <div className="flex items-start gap-2.5">
                          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            {flag.clause && (
                              <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide mb-0.5">
                                {flag.clause}
                              </p>
                            )}
                            <p className="text-sm text-gray-900">{flag.issue}</p>
                            {flag.recommendation && (
                              <p className="text-xs text-gray-500 mt-1 flex items-start gap-1">
                                <ArrowRight className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                <span>{flag.recommendation}</span>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Must Clarify */}
              {hasClarifications && (
                <div>
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-2">{t('fullAnalysis.mustClarify')}</p>
                  <div className="space-y-1.5">
                    {mustClarify.map((question: string, idx: number) => (
                      <div key={idx} className="flex items-start gap-2.5 py-1.5">
                        <span className="w-5 h-5 rounded bg-gray-900 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                          {idx + 1}
                        </span>
                        <span className="text-sm text-gray-700">{question}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Missing Docs */}
              {missingDocs.length > 0 && (
                <div>
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-2">{t('fullAnalysis.missingDocuments')}</p>
                  <div className="space-y-1">
                    {missingDocs.map((doc: string, idx: number) => (
                      <div key={idx} className="flex items-center gap-2 py-1">
                        <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{doc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Work Plan */}
              {hasWorkPlan && (
                <div>
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-2">{t('fullAnalysis.workPlan')}</p>
                  <div className="space-y-1.5">
                    {[...workPlan, ...nextSteps.map((s: any) => s.title || s), ...recommendedActions].filter(Boolean).map((step: any, idx: number) => (
                      <div key={idx} className="flex items-start gap-2.5 p-2.5 rounded-lg bg-gray-50">
                        <span className="w-5 h-5 rounded bg-gray-900 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                          {idx + 1}
                        </span>
                        <span className="text-sm text-gray-700">{typeof step === 'string' ? step : step.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Suggested Response */}
              {brief.suggested_response && (
                <div>
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-2">{t('fullAnalysis.suggestedResponse')}</p>
                  <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{brief.suggested_response}</p>
                  </div>
                </div>
              )}
              
            </div>
          </div>
          
          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 bg-white">
            <div className="flex gap-3">
              {onGenerateReply && (
                <Button
                  onClick={onGenerateReply}
                  className="flex-1 h-10 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-lg gap-2"
                >
                  <MessageSquare className="w-4 h-4" />
                  {t('triagePanel.generateReply')}
                </Button>
              )}
              
              {onCreateTasks && (
                <Button
                  onClick={onCreateTasks}
                  variant="outline"
                  className="flex-1 h-10 text-sm font-medium rounded-lg gap-2 border-gray-200 hover:bg-gray-50 text-gray-700"
                >
                  <ListChecks className="w-4 h-4" />
                  {t('fullAnalysis.createTaskList')}
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FullAnalysisModal;
